import { ArrowDownward } from "@mui/icons-material";
import { Box, Chip, CircularProgress, Typography, useTheme } from "@mui/material";

import { Timings } from "@storyboard/web-logger/timings/Timings";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { createLogger } from "@/logger";

const log = createLogger("feed");

import { eq } from "drizzle-orm";
import { useLiveQuery } from "@tanstack/react-db";
import { Profiler, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import FeedItem from "@/components/feed/FeedItem";
import { estimateFeedRowSize } from "@/components/feed/feedRowSize";
import TmsEventChip from "@/components/feed/tmsEvent/TmsEventChip";
import {
  feedFooterHeight,
  footerTransitionSx,
  globalAudioPlayerHeight,
  topHeaderHeight,
} from "@/components/NewDesign/newDesignUtils.ts";
import VirtualizedList from "@/components/VirtualizedList.tsx";
import { ampli } from "@/config";
import Locator from "@/locator.ts";
import { ActionContext } from "@/models/ActionsProvider";
import { AudioAppContext } from "@/models/AudioAppContextProvider.tsx";
import {
  canSkipInitialTailFetch,
  type FeedFetchAnchor,
  fetchFeedItems,
  localFeedCache,
  shouldPageOlderOnOpen,
} from "@/models/actions/feedItems";
import { downloadFeedItemContent } from "@/models/actions/initialFeedLoad";
import { DataContext } from "@/models/DataProvider";
import { FEED_WINDOW_PAGE_SIZE, FeedContext, type FullItem } from "@/models/FeedContextProvider";
import { type FeedRow, feedRowIndexes } from "@/models/feed/feedRows";
import { feedHeldThroughLogHead } from "@/models/feedItem/feedItemSync";
import { fetchFeedCommandAliasesQuery } from "@/models/repositories";
import { resetOrphanedLoadedContent } from "@/models/repositories/Item";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { actions, TelemetryContext } from "@/models/TelemetryProvider";
import { profilerOnRender } from "@/services/perf/profilerOnRender";
import { store } from "@/store/store";
import FeedItemHeader from "@/ui/FeedItemHeader";

const highlightTimeout = 5000;

export default function FeedVirtualizedList({
  hasGlobalAudioPlayer,
  currentFeedDate,
}: {
  hasGlobalAudioPlayer: boolean;
  currentFeedDate: Date;
}) {
  const { feedMap, feedRows, vListRef, currentFeed, expandFeedWindow, windowFullyLoaded, setFeedWindowStartTimestamp } =
    useContext(FeedContext);
  const theme = useTheme();
  const { myAccount } = useContext(MyAccountContext);
  const { trackAction, finishAction } = useContext(TelemetryContext);
  const { preferredLanguage } = useContext(DataContext);
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const { accountEvent } = useContext(ActionContext);
  const location = useLocation();
  const navigate = useNavigate();
  const feedItemId = new URLSearchParams(location.search).get("feedItemId") ?? null;
  const hashValue = location.hash?.split("#")?.[1] ?? null;
  const hasContentId = hashValue || feedItemId;
  const [activeHashOrFeedItemId, _setActiveHashOrFeedItemId] = useState<string | null>(hasContentId);
  const activeHashOrFeedItemIdRef = useRef<string | null>(hasContentId);
  const setActiveHashOrFeedItemId = (value: string | null | (() => string | null)) => {
    const resolved = typeof value === "function" ? value() : value;
    activeHashOrFeedItemIdRef.current = resolved;
    _setActiveHashOrFeedItemId(resolved);
  };
  const [activeFeedItemIndex, setActiveFeedItemIndex] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [range, setRange] = useState<number[]>([0, 0]);
  const [showScrollToBottom, setShowScrollToBottom] = useState<boolean>(false);
  const [allowJumpButton, setAllowJumpButton] = useState<boolean>(false);
  const [preventScroll, setPreventScroll] = useState<boolean>(false);
  // Whether the reader is at the newest end of the feed. A live arrival or an unread flip pins the feed to the newest
  // row only while this holds: a reader who has scrolled back a week is not pulled to the bottom by a new message.
  // Written only when a scroll ends, from the range that scroll left: the range also changes when rows append, and
  // reading it then would clear the flag for a reader pinned to the bottom before the pinning effects ran. A ref, not
  // state, so those effects keep their dependency arrays and read the latest value.
  const atNewestRef = useRef<boolean>(true);
  const lastRangeRef = useRef<{ startIndex: number; endIndex: number } | null>(null);

  // Convert from useState to useRef
  const isLoadedFirstPageRef = useRef<boolean>(currentFeed?.loadedFirstPage || false);
  const isLoadedLastPageRef = useRef<boolean>(currentFeed?.loadedLastPage || false);
  const isLoadingItemsRef = useRef<boolean>(false);
  const initialItemLoadRef = useRef<boolean>(true);

  const feedMapRef = useRef(feedMap);
  feedMapRef.current = feedMap;

  // Track fetched timestamp ranges to avoid re-fetching
  const oldestFetchedTimestampRef = useRef<string | null>(null);
  const newestFetchedTimestampRef = useRef<string | null>(null);

  const initialContentLoad = useRef<boolean>(true);
  // `${id}:${language}` keys downloaded since the feed opened: a language change clears every item's loaded content,
  // and the new language has to be asked for again.
  const downloadedContentIds = useRef(new Set<string>());
  const [fetchDoneFeedId, setFetchDoneFeedId] = useState<string | null>(null);
  const countThreshold = 5;
  const feedId = currentFeed?.id;
  const currentFeedItems = feedMap?.[0]?.feedId === feedId ? feedMap : [];
  // The list renders rows (messages with the driver's TMS event chips between them); every effect below still
  // reasons in items, so the two index spaces are mapped and the maps read through a ref like feedMap is.
  const currentFeedRows: FeedRow[] = feedMap?.[0]?.feedId === feedId ? (feedRows ?? []) : [];
  const rowIndexes = useMemo(() => feedRowIndexes(currentFeedRows), [currentFeedRows]);
  const rowsRef = useRef({ rows: currentFeedRows, ...rowIndexes });
  rowsRef.current = { rows: currentFeedRows, ...rowIndexes };
  const rowIndexOf = (itemIndex: number): number => rowsRef.current.rowIndexOfItem[itemIndex] ?? itemIndex;
  const itemIndexOf = (rowIndex: number): number => rowsRef.current.itemIndexOfRow[rowIndex] ?? rowIndex;
  const fetchDone = fetchDoneFeedId === feedId;
  const workspaceId = currentWorkspaceId;
  const pageSize = 50;

  // Scroll-back pages and local-window expansions grow feedMap at its older end. The length-keyed
  // scroll effects below exist to pin the feed to the newest message on open and on live arrivals,
  // so they must sit out a prepend or they yank the user back to the bottom mid-scroll-back.
  // Detect the growth direction by watching the edge items across renders. The effects read the
  // render-scoped pulse but deliberately exclude it from their dep arrays: adding it would re-fire
  // them when it drops back to false next render, performing the exact jump the gate prevents.
  const previousEdgeItemsRef = useRef<{
    feedId: string | undefined;
    oldestItemId: string | null;
    newestItemId: string | null;
  }>({ feedId: undefined, oldestItemId: null, newestItemId: null });
  const oldestLoadedItemId = currentFeedItems?.[0]?.id ?? null;
  const newestLoadedItemId = currentFeedItems?.[currentFeedItems.length - 1]?.id ?? null;
  const grewOlderItems =
    previousEdgeItemsRef.current.feedId === feedId &&
    previousEdgeItemsRef.current.oldestItemId !== null &&
    oldestLoadedItemId !== null &&
    oldestLoadedItemId !== previousEdgeItemsRef.current.oldestItemId;
  useEffect(() => {
    previousEdgeItemsRef.current = { feedId, oldestItemId: oldestLoadedItemId, newestItemId: newestLoadedItemId };
  });

  const { data: aliasChannelRows } = useLiveQuery((q) =>
    currentFeed?.id ? fetchFeedCommandAliasesQuery(currentFeed.id)(q) : null,
  );
  const aliasChannelDetails = aliasChannelRows ?? [];

  const aliasChannelOwnerId = aliasChannelDetails?.[0]?.id ?? null;

  const feedMapIds = useMemo(
    () => feedMap?.filter((feedItem) => feedItem?.id !== feedId)?.map((feedItem) => feedItem.id),
    [feedMap, feedId],
  );

  const firstNewItem = useMemo(() => {
    const newCount = feedMap
      ?.filter((item) => item.accountId !== myAccount?.id)
      ?.map((item) => ({ id: item.id, createdAt: item.createdAt }))
      .filter((item) => {
        const dateCheck = new Date(item.createdAt).getTime() - new Date(currentFeedDate).getTime();
        return dateCheck > 0;
      });
    return feedMap?.[feedMap?.length - newCount?.length];
  }, [feedMap, currentFeedDate]);

  useEffect(() => {
    if (firstNewItem?.id) {
      const findFeedMapRefIndex = feedMapRef.current.findIndex((item) => item.id === firstNewItem.id);
      if (findFeedMapRefIndex > -1) {
        setActiveIndex(() => findFeedMapRefIndex);
      }
    }
  }, [firstNewItem]);

  const removeHash = (hashId: string) => {
    const hash = window.location.hash;
    const search = window.location.search?.replace("?feedItemId=", "");
    const hasHashValue = hash?.length > 0 ? hash?.replace("#", "") : search?.length > 0 ? search : null;

    if (!hasHashValue || hasHashValue?.length === 0 || hasHashValue !== hashId) {
      return;
    }

    if (window.location.hash || window.location.search?.includes("?feedItemId=")) {
      navigate(window.location.pathname, { replace: true });
      setActiveFeedItemIndex(() => null);
    }
    setActiveHashOrFeedItemId(() => null);
  };

  const setPreventScrollAction = () => {
    setPreventScroll(() => true);
    setTimeout(() => {
      setPreventScroll(() => false);
    }, 500);
  };

  // Phase 1: Fetch feed items based on pagination
  const fetchFeedItemsPage = async ({
    direction,
    anchor,
    referenceItemId,
  }: {
    direction: "newer" | "older";
    anchor: FeedFetchAnchor;
    referenceItemId?: string;
  }) => {
    isLoadingItemsRef.current = true;

    try {
      const spanName = initialContentLoad.current ? actions.feedInitial(currentFeed) : actions.feedScroll(currentFeed);
      trackAction(spanName, {
        feedId,
        direction,
        anchor,
        referenceItemId: referenceItemId || "none",
      });

      log.debug("[FETCH ITEMS] fetchFeedItemsPage", {
        direction,
        anchor,
        referenceItemId,
        items: feedMapRef.current?.length,
      });

      const result = await fetchFeedItems({
        workspaceId,
        feedId,
        direction,
        pageSize,
        anchor,
        referenceItemId,
      });

      log.debug("[FETCH ITEMS] fetched feed items", {
        direction,
        anchor,
        referenceItemId,
        result,
      });

      // Update pagination state based on results. A failed or skipped request returns no items but
      // proves nothing about how much history is left, so it must not retire either end.
      if (result.failed || result.skipped) {
        log.debug("[FETCH ITEMS] fetch failed or skipped, leaving pagination state untouched", { direction, anchor });
      } else if (direction === "newer" && result.lastPage) {
        isLoadedLastPageRef.current = true;
      } else if (direction === "older" && result.lastPage) {
        isLoadedFirstPageRef.current = true;
        if (anchor === "tail") {
          isLoadedLastPageRef.current = true;
        }
      }

      // Scroll to newest items if this is the initial load and no specific item ID
      if (initialItemLoadRef.current && direction === "newer" && !activeHashOrFeedItemIdRef.current) {
        // Defer scrolling until after re-render
        setTimeout(() => {
          scrollToNewestRow();
        }, 100);
      }

      initialItemLoadRef.current = false;
      finishAction(spanName);
      return result;
    } catch (error) {
      console.error("Error fetching feed items:", error);
    } finally {
      isLoadingItemsRef.current = false;
    }
  };

  // Phase 2: Download content for visible items
  const downloadFeedItems = async () => {
    const isRangeUninitialized = range[0] === 0 && range[1] === 0 && feedMapRef.current?.length > 0;
    const [effectiveLower, effectiveUpper] = isRangeUninitialized
      ? [Math.max(0, feedMapRef.current.length - 50), feedMapRef.current.length]
      : range;
    const lowerBound = Math.max(0, effectiveLower - 20);
    const upperBound = Math.min(feedMapRef.current?.length, effectiveUpper + 20);
    const feedMapRange = feedMapRef.current.slice(lowerBound, upperBound);

    const recentCutoff = Date.now() - 5 * 60 * 1000;
    const needsContentDownload = (item: FullItem) => {
      if (!item.loadedContent) return true;
      const isRecent = new Date(item.createdAt).getTime() > recentCutoff;
      if (!isRecent) return false;
      return (
        !item.files?.length &&
        !item.audioEncodings?.length &&
        !item.transcriptions?.length &&
        !item.links?.length &&
        !item.callRecords?.length &&
        !item.displayArtifact
      );
    };
    const idMap = feedMapRange
      .filter(
        (item) => needsContentDownload(item) && !downloadedContentIds.current.has(`${item.id}:${preferredLanguage}`),
      )
      .map((item) => item.id)
      .filter(Boolean);

    const spanName = initialContentLoad.current ? actions.feedInitial(currentFeed) : actions.feedScroll(currentFeed);

    if (idMap.length > 0) {
      log.info("[ON-DEMAND CONTENT] Downloading content for visible items", {
        feedId,
        feedName: currentFeed?.title,
        itemCount: idMap.length,
        itemIds: idMap,
        isInitial: initialContentLoad.current,
        range: { lower: lowerBound, upper: upperBound },
      });

      trackAction(spanName, {
        feedId,
        feedName: currentFeed?.title,
        "feed.itemsLoaded": idMap.length.toString(),
      });
      const isInitialLoad = initialContentLoad.current;
      initialContentLoad.current = false;

      await downloadFeedItemContent(workspaceId, idMap, preferredLanguage);
      for (const id of idMap) downloadedContentIds.current.add(`${id}:${preferredLanguage}`);
      finishAction(spanName);

      Timings.createEvent("feed-content-painted", {
        telemetryCategory: "perceived",
        properties: { feedId, feedName: currentFeed?.title, itemCount: idMap.length },
      });

      log.info("[ON-DEMAND CONTENT] Completed downloading content", {
        feedId,
        feedName: currentFeed?.title,
        itemCount: idMap.length,
      });

      if (isInitialLoad && !activeHashOrFeedItemIdRef.current) {
        setTimeout(() => {
          scrollToNewestRow();
        }, 150);
      }
    }
  };

  // Check if we need to load more items when approaching the edges of our loaded set
  const checkForMoreItems = async () => {
    if (isLoadingItemsRef.current) {
      log.debug("[FETCH ITEMS] checkForMoreItems - already loading, skipping");
      return;
    }
    if (initialItemLoadRef.current) {
      log.debug("[FETCH ITEMS] checkForMoreItems - initial load in progress, skipping");
      return;
    }

    // The list renders oldest-first, so the top of the viewport is the oldest end of history and
    // the bottom is the live tail. Both branches anchor on the feed's proven-contiguous span
    // rather than on feedMap: once the render window has leapt a hole in the local cache, its
    // edges sit inside a stale island and paging from them skips the missing range forever.
    if (range[0] <= 10) {
      // Expand the local-DB query window first so any older rows already
      // cached locally (from prior visits or eager warmup) become
      // visible immediately, without waiting for an API round-trip.
      if (!windowFullyLoaded) {
        log.debug("[FETCH ITEMS] checkForMoreItems - expanding local window");
        expandFeedWindow();
      }

      if (!isLoadedFirstPageRef.current) {
        // A feed with no span yet has nothing to page back from, so the fetch falls through to a
        // tail request that seeds one. Dedupe on that state too, or every scroll-end refetches it.
        const contiguityFloor = currentFeed?.oldestContiguousCreatedAt ?? "tail";
        if (contiguityFloor !== oldestFetchedTimestampRef.current) {
          log.debug("[FETCH ITEMS] checkForMoreItems - fetching older", {
            contiguityFloor,
            previousFetched: oldestFetchedTimestampRef.current,
          });
          oldestFetchedTimestampRef.current = contiguityFloor;
          isLoadingItemsRef.current = true; // Set immediately to prevent concurrent calls
          const result = await fetchFeedItemsPage({ direction: "older", anchor: "contiguity" });
          // A failed page proved nothing and moved no bound, so the dedupe marker set above would
          // block every retry for the session. Drop it so a later scroll-end tries again.
          if (!result || result.failed || result.skipped) {
            oldestFetchedTimestampRef.current = null;
          }
          return; // Exit early to avoid checking for newer items in the same call
        }
        log.debug("[FETCH ITEMS] checkForMoreItems - older items already fetched", {
          contiguityFloor,
        });
      }
    }

    const nearBottom = feedMapRef.current?.length > 0 && range[1] >= feedMapRef.current.length - 10;
    // A feed with no span yet has no trusted tail to walk forward from; the top branch's fallback
    // seeds one, and until then the live tail is whatever AppSync has delivered.
    const contiguityCeiling = currentFeed?.newestContiguousCreatedAt;
    if (!nearBottom || !contiguityCeiling || isLoadedLastPageRef.current) {
      return;
    }

    if (contiguityCeiling !== newestFetchedTimestampRef.current) {
      log.debug("[FETCH ITEMS] checkForMoreItems - fetching newer", {
        contiguityCeiling,
        previousFetched: newestFetchedTimestampRef.current,
      });
      newestFetchedTimestampRef.current = contiguityCeiling;
      isLoadingItemsRef.current = true; // Set immediately to prevent concurrent calls
      const result = await fetchFeedItemsPage({ direction: "newer", anchor: "contiguity" });
      if (!result || result.failed || result.skipped) {
        newestFetchedTimestampRef.current = null;
      }
    } else {
      log.debug("[FETCH ITEMS] checkForMoreItems - newer items already fetched", {
        contiguityCeiling,
      });
    }
  };

  /** Scrolls the list to an item, by the item's own index. */
  const scrollToIndex = (index: number) => {
    if (preventScroll) {
      setTimeout(() => setPreventScroll(() => false), 250);
      return;
    }
    vListRef.current?.scrollToIndex(rowIndexOf(index), { align: "start" });
    setAllowJumpButton(() => true);
  };

  /** Scrolls to the newest row, which is a chip when an event is newer than the last message. */
  const scrollToNewestRow = () => {
    if (preventScroll) {
      setTimeout(() => setPreventScroll(() => false), 250);
      return;
    }
    vListRef.current?.scrollToIndex(rowsRef.current.rows.length - 1, { align: "start" });
    setAllowJumpButton(() => true);
  };

  /** The visible range arrives in row indices; the download and paging effects read it in items. */
  const handleRangeChange = ({ startIndex, endIndex }: { startIndex: number; endIndex: number }) => {
    const lastRow = rowsRef.current.rows.length - 1;
    lastRangeRef.current = { startIndex, endIndex };
    if (endIndex === lastRow) {
      setShowScrollToBottom(() => false);
    } else {
      setShowScrollToBottom(() => endIndex <= lastRow && rowsRef.current.rows.length > countThreshold);
    }
    const range = startIndex > 0 ? [itemIndexOf(startIndex) - 5, itemIndexOf(endIndex) + 5] : [0, 10];
    setRange(() => range);
  };

  const findFirstUnreadIndex = feedMapRef.current?.findIndex((feedItem) => feedItem.unread === true);
  const unreadCount = feedMapRef.current?.filter((item) => item.unread)?.length;

  const jumpToLastItem = () => {
    scrollToNewestRow();
    setPreventScrollAction();
  };

  /**
   * useEffects ➞
   *    0. Initial items fetch on feed change
   *    1. Download Feed Items
   *    2. Scroll to unread when no deeplink
   *    3. Reset UI on feed change (preserve deeplink state when URL has hash/query)
   *    4. Set unread active item
   *    5. Sync active hash from location
   *    6. Scroll to deeplinked item as the map fills
   *    7. Clear URL after highlight dwell (not tied to map churn)
   *    8. Mark unread items / scroll to bottom when no deeplink
   */

  // 0. Initial items fetch when feed changes, when component mounts, or when a
  //    search result navigates to the same feed with a new feedItemId.
  useEffect(() => {
    let cancelled = false;

    log.debug("[FETCH ITEMS] initial load", {
      feedId,
      currentFeedItems: currentFeedItems.length,
      loadedLastPage: currentFeed?.loadedLastPage,
      hasContentId,
    });

    isLoadedFirstPageRef.current = currentFeed?.loadedFirstPage || false;
    isLoadedLastPageRef.current = currentFeed?.loadedLastPage || false;
    initialContentLoad.current = !currentFeedItems.length;
    isLoadingItemsRef.current = false;
    initialItemLoadRef.current = true;
    downloadedContentIds.current = new Set();
    setFetchDoneFeedId(null);
    oldestFetchedTimestampRef.current = null;
    newestFetchedTimestampRef.current = null;

    const init = async () => {
      if (feedId) {
        await resetOrphanedLoadedContent(feedId);
      }
      if (cancelled) return;

      if (hasContentId) {
        setActiveHashOrFeedItemId(hasContentId);
        log.debug("[FETCH ITEMS] deeplink fetch");
        await fetchFeedItemsPage({ direction: "newer", anchor: "item", referenceItemId: hasContentId });
        if (cancelled) return;
        await fetchFeedItemsPage({ direction: "older", anchor: "item", referenceItemId: hasContentId });
        if (cancelled) return;
        // Anchor the local-DB window at the deeplinked item so it (and
        // everything newer) is guaranteed to be in feedMap when the
        // virtual list scrolls to it. Without this the default newest-page
        // window would hide deeplinks that point to older items.
        const deeplinkedAt = store.get("item", hasContentId)?.createdAt;
        if (!cancelled && deeplinkedAt) {
          setFeedWindowStartTimestamp(deeplinkedAt);
        }
        if (!cancelled) setFetchDoneFeedId(feedId);
      } else {
        // Read from the store index, not the window-gated live-query results: those are empty
        // until the window probe resolves, which would read as a hole on every open.
        const cache = localFeedCache(feedId);
        const heldThroughHead = feedHeldThroughLogHead(workspaceId, currentFeed);
        const skip = canSkipInitialTailFetch({
          loadedLastPage: currentFeed?.loadedLastPage ?? false,
          loadedFirstPage: currentFeed?.loadedFirstPage ?? false,
          cache,
          spanFloor: currentFeed?.oldestContiguousCreatedAt ?? null,
          spanCeiling: currentFeed?.newestContiguousCreatedAt ?? null,
          windowSize: FEED_WINDOW_PAGE_SIZE,
          heldThroughHead,
        });
        if (skip) {
          // The item log holds the feed up to its head, so there is nothing newer to page forward to.
          if (heldThroughHead) isLoadedLastPageRef.current = true;
          log.debug("[FETCH ITEMS] skip - feed already fully loaded", {
            itemCount: currentFeedItems.length,
          });
          initialItemLoadRef.current = false;
          if (
            shouldPageOlderOnOpen({
              heldThroughHead,
              loadedFirstPage: currentFeed?.loadedFirstPage ?? false,
              count: cache.count,
              pageSize: FEED_WINDOW_PAGE_SIZE,
            })
          ) {
            await fetchFeedItemsPage({ direction: "older", anchor: "contiguity" });
            if (cancelled) return;
          }
          setFetchDoneFeedId(feedId);
        } else {
          if (currentFeed?.loadedLastPage) {
            // The persisted bookkeeping promised a cache the store no longer holds (eviction, a
            // dropped persist batch — WEB-2254). The same evidence impeaches all of it — a kept
            // `loadedFirstPage` would leave scroll-back disabled below the hole, and a kept span
            // would absorb the refetched page instead of restarting under it — so clear the flags
            // and the span together and let the tail fetch below reseed them from what it returns.
            log.info("[FETCH ITEMS] loadedLastPage not backed by local rows; refetching tail", {
              feedId,
              cache,
            });
            store.update("feed", feedId, (row) => ({
              ...row,
              loadedLastPage: false,
              loadedFirstPage: false,
              oldestContiguousCreatedAt: null,
              newestContiguousCreatedAt: null,
            }));
            isLoadedLastPageRef.current = false;
            isLoadedFirstPageRef.current = false;
          }
          log.debug("[FETCH ITEMS] fetching items");
          await fetchFeedItemsPage({ direction: "older", anchor: "tail" });
          if (!cancelled) setFetchDoneFeedId(feedId);
        }
      }
    };
    init();

    return () => {
      cancelled = true;
    };
  }, [feedId, hasContentId]);

  // 1. Download feed items content
  useEffect(() => {
    if (fetchDone) {
      downloadFeedItems();
    }
  }, [preferredLanguage, feedId, range, fetchDone, currentFeedItems.length]);

  // Local eviction can prune history out from under an open channel. The session flag is only
  // seeded on feed change, so clear it when the persisted one drops or scroll-back stays disabled
  // for a feed that no longer holds its oldest page.
  useEffect(() => {
    if (currentFeed?.loadedFirstPage === false) {
      isLoadedFirstPageRef.current = false;
    }
  }, [currentFeed?.loadedFirstPage]);

  // 2. Scroll to unread items when they appear (skip when navigating to a specific item, or when the reader has
  //    scrolled back into history)
  useEffect(() => {
    if (unreadCount > 0 && !activeHashOrFeedItemId && !grewOlderItems && atNewestRef.current) {
      scrollToNewestRow();
    }
  }, [unreadCount, feedMapIds?.length]);

  // 3. Reset values
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const feedItemIdFromQuery = searchParams.get("feedItemId");
    const hashFragment = window.location.hash?.split("#")?.[1] ?? null;
    const deeplinkId = hashFragment || feedItemIdFromQuery;

    setActiveFeedItemIndex(() => null);
    setPreventScroll(() => false);
    setShowScrollToBottom(() => false);
    setAllowJumpButton(() => false);
    // A freshly opened feed lands at its newest row; the first scroll end re-derives this from the visible range.
    atNewestRef.current = true;
    lastRangeRef.current = null;
    if (!deeplinkId) {
      setActiveHashOrFeedItemId(() => null);
    }
    setActiveIndex(() => -1);
    initialContentLoad.current = true;
    ampli.feedOpen({ feedId });
  }, [feedId]);

  // 4. Set unread active item
  useEffect(() => {
    setActiveIndex(() => findFirstUnreadIndex);
  }, [feedMapRef.current?.length]);

  // 5. Sync active hash / feedItemId from the location (hash or ?feedItemId=)
  useEffect(() => {
    if (hasContentId) {
      setActiveHashOrFeedItemId(() => hasContentId);
    }
  }, [hasContentId]);

  // 6. Scroll to deeplinked item once it appears in the map (re-run as the window fills).
  useEffect(() => {
    if (!activeHashOrFeedItemId) {
      return;
    }
    const hashId = activeHashOrFeedItemId;
    const scheduledFeedId = feedId;
    const scrollTimer = window.setTimeout(() => {
      const scrollPosition = feedMapRef.current?.findIndex((feedItem) => feedItem.id === hashId);
      if (scrollPosition !== undefined && scrollPosition >= 0) {
        scrollToIndex(scrollPosition);
        setActiveFeedItemIndex(() => `${scheduledFeedId}:${rowIndexOf(scrollPosition)}`);
      }
    }, 250);
    return () => {
      clearTimeout(scrollTimer);
    };
  }, [feedId, activeHashOrFeedItemId, feedMapIds]);

  // 7. Strip the URL and clear highlight after a fixed dwell time (not tied to feedMap churn).
  useEffect(() => {
    if (!activeHashOrFeedItemId) {
      return;
    }
    const hashId = activeHashOrFeedItemId;
    const removeTimer = window.setTimeout(() => {
      removeHash(hashId);
    }, highlightTimeout);
    return () => {
      clearTimeout(removeTimer);
    };
  }, [feedId, activeHashOrFeedItemId]);

  // 8. Mark unread items ➞ Scroll. Sits out a prepend, and a live arrival while the reader is scrolled back: the
  //    "Jump to latest message" chip is their way down, not a yank.
  useEffect(() => {
    if (grewOlderItems || !atNewestRef.current) {
      return;
    }
    if (!activeHashOrFeedItemId && feedMapRef.current?.length > 0) {
      if (findFirstUnreadIndex > 0) {
        scrollToIndex(findFirstUnreadIndex);
        setActiveIndex(() => findFirstUnreadIndex);
      } else {
        scrollToNewestRow();
      }
    }
  }, [feedMapRef.current?.length, feedId]);

  const JumpToLatestMessage = () => {
    return (
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          bottom: "1rem",
          left: 0,
          display: "flex",
          zIndex: 300,
        }}
      >
        <Box
          sx={{
            maxWidth: "1300px",
            margin: "0 auto",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Chip
            id="jumpToLatestMessage"
            role="button"
            onClick={() => jumpToLastItem()}
            sx={{
              background: theme.palette.brand.secondary.main,
              "&:hover": { background: theme.palette.brand.secondary.light },
            }}
            label={
              <Box sx={{ display: "flex", alignItem: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ArrowDownward role="img" sx={{ fontSize: "1rem", mr: 1 }} />
                </Box>
                <Box>Jump to latest message</Box>
              </Box>
            }
          />
        </Box>
      </Box>
    );
  };
  const scrollEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /** The date header over a row when it opens a day, or nothing. */
  const dateHeader = (dateId: string, dateLabel: string) => (
    <FeedItemHeader
      isStuck={false}
      dateId={dateId}
      onClick={() => {}}
      dateLabel={dateLabel}
      isUnreadHeader={false}
      unreadCount={0}
    />
  );

  const renderChild = (row: FeedRow, index: number) => {
    if (!row) {
      return <></>;
    }

    const isLastRow = index === rowsRef.current.rows.length - 1;

    // A day opens with its own row, so a page of older history that moves the start of the day never resizes the
    // message or chip that used to carry the header.
    if (row.type === "dateHeader") {
      return (
        <Box key={row.key} sx={{ maxWidth: "none", margin: "0 auto" }}>
          {dateHeader(row.dateId, row.label)}
        </Box>
      );
    }

    if (row.type === "tmsEvent") {
      return (
        <Box key={row.key} sx={{ maxWidth: "none", margin: "0 auto" }}>
          <div style={{ paddingBottom: "8px", ...(isLastRow ? {} : { marginBottom: "8px" }) }}>
            <TmsEventChip event={row.event} />
          </div>
        </Box>
      );
    }

    const feedItem = row.item;

    return (
      <Box
        key={`feed-item-${feedItem?.id}`}
        sx={{
          maxWidth: "none",
          margin: "0 auto",
        }}
      >
        {activeIndex === itemIndexOf(index) && (
          <Box
            data-sb={TestIds.feed.unreadDivider}
            sx={{
              width: "100%",
              textAlign: "right",
              borderBottom: `2px solid ${theme.palette.brand.secondary.main}`,
              fontSize: "12px",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: theme.palette.brand.secondary.main,
              }}
            >
              New
            </Typography>
          </Box>
        )}
        <div
          style={{
            paddingBottom: "16px",
            ...(isLastRow ? {} : { marginBottom: "16px" }),
          }}
        >
          <FeedItem
            preventScroll={(_value: boolean) => {
              setPreventScrollAction();
            }}
            setItemNotLoaded={async () => downloadFeedItems()}
            feed={currentFeed}
            item={feedItem}
            vListIndex={index}
            aliasChannelOwnerId={aliasChannelOwnerId}
            active={`${feedId}:${index}` === activeFeedItemIndex}
            highlightPodcast={`${feedId}:${index}` === activeFeedItemIndex}
          />
        </div>
      </Box>
    );
  };

  const footerBox = feedFooterHeight;
  const feedListInset = -10;
  const calculatedHeightNumber = hasGlobalAudioPlayer
    ? footerBox + topHeaderHeight + globalAudioPlayerHeight + feedListInset
    : footerBox + topHeaderHeight + feedListInset;

  const calculatedHeight = `calc(100vh - ${calculatedHeightNumber}px)`;
  const { activeItemId } = useContext(AudioAppContext);
  useEffect(() => {
    if (activeItemId) {
      setTimeout(() => {
        const playingFeedItem = document.getElementById(`feed-item-${activeItemId}`);
        if (playingFeedItem) {
          playingFeedItem.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }
      }, 250);
    }
  }, [activeItemId]);

  return (
    <Profiler id="FeedVirtualizedList" onRender={profilerOnRender}>
      <Box
        sx={{
          position: "relative",
          height: calculatedHeight,
          paddingRight: 0,
          paddingLeft: 0,
          ...footerTransitionSx,
        }}
        aria-label={Locator.feed.items.list}
        data-sb={TestIds.feed.itemList}
      >
        {showScrollToBottom && allowJumpButton && <JumpToLatestMessage />}
        <VirtualizedList
          refValue={vListRef}
          id="vListRef"
          reverse={true}
          data={currentFeedRows}
          itemSize={estimateFeedRowSize}
          childComponent={renderChild}
          emptyState={
            fetchDone ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: theme.palette.primary.main,
                }}
              >
                <Typography variant="h6" fontWeight={700}>
                  There are no messages yet
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <CircularProgress size={32} />
              </Box>
            )
          }
          onScroll={(value: { startIndex: number; endIndex: number }) => handleRangeChange(value)}
          onScrollEnd={() => {
            // A scroll has settled: the reader is at the newest end when the visible range reaches the last row.
            const range = lastRangeRef.current;
            if (range) atNewestRef.current = range.endIndex >= rowsRef.current.rows.length - 1;
            if (scrollEndTimeoutRef.current) {
              clearTimeout(scrollEndTimeoutRef.current);
            }
            scrollEndTimeoutRef.current = setTimeout(() => {
              checkForMoreItems().then(() => downloadFeedItems());
            }, 100);
          }}
        />
      </Box>
    </Profiler>
  );
}
